import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDateTime } from '@/utils/date';
import { PCMP_LANE_TONE_COLOR } from '../../constants/pcmpProposal.constants';
import type { PcmpLaneCell } from '../../types/pcmpProposal.types';
import type { PcmpQueueItem, PcmpQueuePerson } from '../../types/pcmpQueue.types';
import { PcmpStatusPill } from '../PcmpProposalDetail/PcmpStatusPill';

export function PcmpEmptyCell() {
  return <span className="text-muted-foreground">{EMPTY_VALUE}</span>;
}

export function PersonCell({ person }: { person: PcmpQueuePerson | null }) {
  if (!person) return <PcmpEmptyCell />;
  const meta = [person.hrEmpId, person.designationName].filter(Boolean).join(' · ');
  return (
    <div className="flex max-w-56 flex-col">
      <span className="truncate font-medium" title={person.fullName}>
        {person.fullName}
      </span>
      {meta ? <span className="truncate text-xs text-muted-foreground">{meta}</span> : null}
    </div>
  );
}

export function LaneCell({ cell }: { cell: PcmpLaneCell }) {
  if (!cell.text) return <PcmpEmptyCell />;
  return (
    <div className="flex flex-col gap-1">
      <PcmpStatusPill tone={PCMP_LANE_TONE_COLOR[cell.tone]}>{cell.text}</PcmpStatusPill>
      {cell.sub ? <span className="text-xs text-muted-foreground">{cell.sub}</span> : null}
    </div>
  );
}

interface MisExplanationCellProps {
  item: PcmpQueueItem;
  /** MIS reads the explanation itself; Marketing only needs to know it was given. */
  showText?: boolean;
}

export function MisExplanationCell({ item, showText = false }: MisExplanationCellProps) {
  const notVerified = item.misNotVerified;
  if (!notVerified) return <PcmpEmptyCell />;
  const { explanation } = notVerified;
  const byline = explanation
    ? [explanation.by?.fullName, formatDisplayDateTime(explanation.at)].filter(Boolean).join(' · ')
    : '';
  return (
    <div className="flex max-w-64 flex-col gap-1">
      <span className="truncate text-sm" title={notVerified.reasonLabel ?? undefined}>
        {notVerified.reasonLabel ?? 'Not Verified'}
      </span>
      {explanation ? (
        <span className="flex flex-col gap-0.5">
          <PcmpStatusPill tone="info">Explanation given</PcmpStatusPill>
          {showText && explanation.text ? (
            <span className="line-clamp-2 text-sm whitespace-pre-line" title={explanation.text}>
              {explanation.text}
            </span>
          ) : null}
          <span
            className="truncate text-xs text-muted-foreground"
            title={showText ? undefined : (explanation.text ?? '')}
          >
            {byline}
          </span>
        </span>
      ) : (
        <PcmpStatusPill tone="warning">Awaiting explanation</PcmpStatusPill>
      )}
    </div>
  );
}

interface TagsCellProps {
  hold: PcmpQueueItem['hold'];
  isOptional?: boolean;
}

export function TagsCell({ hold, isOptional = false }: TagsCellProps) {
  if (!hold && !isOptional) return null;
  return (
    <div className="flex flex-col gap-1">
      {hold ? (
        <PcmpStatusPill tone="warning" className="cursor-default">
          <span title={hold.reason ?? undefined}>
            On hold{hold.stageLabel ? ` · ${hold.stageLabel}` : ''}
          </span>
        </PcmpStatusPill>
      ) : null}
      {isOptional ? <PcmpStatusPill tone="neutral">Optional</PcmpStatusPill> : null}
    </div>
  );
}
